// Colour preview for an option value (PRODUCT-001 swatches, 1022): a chip that opens a compact colour
// picker, plus the colour-picker button at the end of the values field (AddColourButton, which adds a
// value named by colourNames). A colour can be typed as a hex code (#C0392B, C0392B or #C33), chosen
// from common colours,
// set with the system colour picker, or sampled from anywhere on screen (EyeDropper, where the browser
// has it). A value may have two colours (a split swatch). Values named after a colour ("Red",
// "Navy blue", "Maroon") get that colour on their own; the merchant can change it.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { t } from "@ady/i18n";
import { cx, Icon } from "@ady/ui";
import { hexForName, nameForHex, uniqueName } from "./colourNames";

const PRESETS = [
  "#000000",
  "#ffffff",
  "#9ca3af",
  "#4b5563",
  "#dc2626",
  "#800000",
  "#ea580c",
  "#f59e0b",
  "#facc15",
  "#16a34a",
  "#065f46",
  "#0ea5e9",
  "#2563eb",
  "#1e3a8a",
  "#7c3aed",
  "#db2777",
  "#f9a8d4",
  "#f5f5dc",
  "#92400e",
  "#d4af37",
];

/** Product colour names that CSS does not know. */
const EXTRA_NAMES: Record<string, string> = {
  cream: "#fffdd0",
  offwhite: "#faf9f6",
  mustard: "#e1ad01",
  wine: "#722f37",
  peach: "#ffcba4",
  rust: "#b7410e",
  charcoal: "#36454f",
  nude: "#e3bc9a",
  skin: "#f1c27d",
  mint: "#98ff98",
  rosegold: "#b76e79",
  golden: "#d4af37",
  mehendi: "#6b7d3a",
  mehndi: "#6b7d3a",
  bottlegreen: "#006a4e",
  coffee: "#6f4e37",
  camel: "#c19a6b",
  burgundy: "#800020",
  multicolor: "",
  multicolour: "",
};

let probe: CanvasRenderingContext2D | null | undefined;
/** Any CSS colour (name, rgb(), hex) → "#rrggbb", or null. */
export function cssToHex(input: string): string | null {
  if (probe === undefined) probe = document.createElement("canvas").getContext("2d");
  if (!probe || !input.trim()) return null;
  // eslint-disable-next-line ady/design-tokens -- a canvas colour probe value, never shown
  probe.fillStyle = "#010203";
  probe.fillStyle = input.trim();
  const out = String(probe.fillStyle).toLowerCase();
  if (out === "#010203" && input.trim().toLowerCase() !== "#010203") return null;
  if (/^#[0-9a-f]{6}$/.test(out)) return out;
  const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(out);
  return m
    ? `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`
    : null;
}

/** A typed hex code → "#rrggbb" (accepts a missing "#" and the 3-digit form), or null. */
export function normHex(s: string): string | null {
  const v = s.trim().replace(/^#/, "").toLowerCase();
  if (/^[0-9a-f]{6}$/.test(v)) return `#${v}`;
  if (/^[0-9a-f]{3}$/.test(v)) return `#${[...v].map((c) => c + c).join("")}`;
  return null;
}

/** The colour a value's name stands for ("Navy blue" → navy), or null when it names none. */
export function guessColour(name: string): string | null {
  const known = hexForName(name.replace(/[-_\s]+/g, " "));
  if (known) return known;
  const n = name
    .toLowerCase()
    .replace(/[^a-z\s-]/g, "")
    .trim();
  if (!n) return null;
  const joined = n.replace(/[\s-]+/g, "");
  if (joined in EXTRA_NAMES) return EXTRA_NAMES[joined] || null;
  const direct = cssToHex(joined);
  if (direct) return direct;
  // "Navy blue" → navy, "Dark green" → green: the first word that names a colour wins.
  for (const w of n.split(/[\s-]+/)) {
    if (w in EXTRA_NAMES) return EXTRA_NAMES[w] || null;
    const hex = cssToHex(w);
    if (hex) return hex;
  }
  return null;
}

/** The swatch fill: one colour, or two split diagonally. */
export const swatchFill = (cols: string[]) =>
  cols.length > 1 ? `linear-gradient(135deg, ${cols[0]} 50%, ${cols[1]} 50%)` : cols[0];

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

/** Closes a popover on a click outside `box` and on Esc (never the Add product popup behind it). */
function usePopover() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (open && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };
  return { open, setOpen, box, onKeyDown };
}

/** System colour picker, hex code, screen eyedropper and common colours for one colour. */
function ColourFields({ value, onPick }: { value: string; onPick: (hex: string) => void }) {
  const [hex, setHex] = useState(value.toUpperCase());
  const [bad, setBad] = useState(false);
  const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  useEffect(() => {
    setHex(value.toUpperCase());
    setBad(false);
  }, [value]);
  const commit = () => {
    if (!hex.trim()) return;
    const v = normHex(hex);
    if (v) onPick(v);
    setBad(!v);
  };
  return (
    <>
      <span className="flex items-center gap-1.5">
        <label
          className="relative size-9 shrink-0 cursor-pointer overflow-hidden rounded-xs border border-control"
          style={value ? { background: value } : undefined}
          title={t("catalog.add.swatchSystem")}
        >
          <input
            type="color"
            value={value || "#cccccc"}
            onChange={(e) => onPick(e.target.value.toLowerCase())}
            aria-label={t("catalog.add.swatchSystem")}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
        </label>
        <input
          value={hex}
          onChange={(e) => {
            setHex(e.target.value);
            const v = normHex(e.target.value);
            if (v) {
              onPick(v);
              setBad(false);
            }
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          placeholder={t("common.samples.hexColour")}
          aria-label={t("catalog.add.swatchHex")}
          aria-invalid={bad || undefined}
          spellCheck={false}
          maxLength={7}
          className={cx(
            "ady-ledger min-h-control min-w-0 flex-1 rounded-xs border bg-surface px-2 text-xs uppercase outline-none focus-visible:outline-2 focus-visible:outline-focus",
            bad ? "border-danger-fg" : "border-control",
          )}
        />
        {EyeDropper ? (
          <button
            type="button"
            onClick={() => {
              new EyeDropper()
                .open()
                .then((r) => {
                  const v = normHex(r.sRGBHex) ?? cssToHex(r.sRGBHex);
                  if (v) onPick(v);
                })
                .catch(() => undefined); // cancelled
            }}
            title={t("catalog.add.swatchEyedropper")}
            className="flex size-9 shrink-0 items-center justify-center rounded-xs border border-control text-link hover:border-link"
          >
            <Icon name="eyedropper" />
            <span className="ady-sr-only">{t("catalog.add.swatchEyedropper")}</span>
          </button>
        ) : null}
      </span>
      {bad ? (
        <span className="text-2xs text-danger-fg">{t("catalog.add.swatchHexBad")}</span>
      ) : null}
      <span
        className="grid grid-cols-10 gap-1"
        role="group"
        aria-label={t("catalog.add.swatchPresets")}
      >
        {PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onPick(c)}
            title={nameForHex(c)}
            aria-label={nameForHex(c)}
            aria-pressed={value === c}
            className={cx(
              "aspect-square rounded-full border",
              value === c ? "ady-outline-brand border-link" : "border-control hover:scale-110",
            )}
            style={{ background: c }}
          />
        ))}
      </span>
    </>
  );
}

const panel =
  "absolute top-full z-30 mt-1 flex w-64 flex-col gap-2 rounded-sm border border-card bg-surface p-2.5 shadow-2";

/** The colour chip of one option value; opens the picker for that value's colour(s). */
export function SwatchPicker({
  value,
  colors,
  onChange,
}: {
  value: string;
  colors: string[];
  onChange: (next: string[]) => void;
}) {
  const { open, setOpen, box, onKeyDown } = usePopover();
  const [slot, setSlot] = useState<0 | 1>(0);
  const put = (c: string) => {
    const next = [...colors];
    if (slot === 1 && !next[0]) next[0] = c;
    next[slot] = c;
    onChange(next.slice(0, 2));
  };
  const split = colors.length > 1;

  return (
    <span ref={box} className="relative" onKeyDown={onKeyDown}>
      <button
        type="button"
        onClick={() => {
          setSlot(0);
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t("catalog.add.swatchFor", { value })}
        className={cx(
          "flex min-h-control items-center gap-1.5 rounded-xs border bg-surface px-1.5 text-xs",
          open ? "border-link" : "border-card hover:border-link",
        )}
        data-testid="swatch-chip"
      >
        <span
          className={cx(
            "size-4 shrink-0 rounded-full border border-control",
            !colors.length && "bg-sunken",
          )}
          style={colors.length ? { background: swatchFill(colors) } : undefined}
        />
        <span data-content="user">{value}</span>
        {colors.length ? null : (
          <span className="text-2xs text-fg-tertiary">{t("catalog.add.swatchNone")}</span>
        )}
      </button>

      {open ? (
        <span
          role="dialog"
          aria-label={t("catalog.add.swatchFor", { value })}
          className={cx(panel, "start-0")}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-semibold" data-content="user">
              {value}
            </span>
            <label className="flex items-center gap-1.5 text-xs text-fg-secondary">
              <input
                type="checkbox"
                checked={split}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([colors[0] ?? "#cccccc", "#ffffff"]);
                    setSlot(1);
                  } else {
                    onChange(colors.slice(0, 1));
                    setSlot(0);
                  }
                }}
              />
              {t("catalog.add.swatchSplit")}
            </label>
          </span>
          {split ? (
            <span className="grid grid-cols-2 gap-1" role="tablist">
              {([0, 1] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  role="tab"
                  aria-selected={slot === n}
                  onClick={() => setSlot(n)}
                  className={cx(
                    "flex min-h-control items-center justify-center gap-1.5 rounded-xs border text-xs",
                    slot === n
                      ? "border-link font-semibold text-link"
                      : "border-card text-fg-secondary",
                  )}
                >
                  <span
                    className="size-3.5 rounded-full border border-control"
                    style={colors[n] ? { background: colors[n] } : undefined}
                  />
                  {n === 0 ? t("catalog.add.swatchFirst") : t("catalog.add.swatchSecondTab")}
                </button>
              ))}
            </span>
          ) : null}
          <ColourFields value={colors[slot] ?? ""} onPick={put} />
          <span className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setSlot(0);
              }}
              className="text-xs font-semibold text-fg-secondary hover:text-danger-fg"
            >
              {t("catalog.add.swatchClear")}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-xs px-2 py-1 text-xs font-semibold text-link hover:bg-hover"
            >
              {t("catalog.add.swatchDone")}
            </button>
          </span>
        </span>
      ) : null}
    </span>
  );
}

/**
 * The colour-picker button at the end of a colour option's values field: pick a colour and it is
 * added as a new value, named after the closest colour (ΔE00; the name can be edited first).
 */
export function AddColourButton({
  taken,
  onAdd,
}: {
  /** The option's current values (a new name never repeats one). */
  taken: string[];
  onAdd: (name: string, hex: string) => void;
}) {
  const { open, setOpen, box, onKeyDown } = usePopover();
  const [hex, setHex] = useState("");
  const [name, setName] = useState("");
  const [named, setNamed] = useState(false); // the merchant typed their own name
  const pick = (c: string) => {
    setHex(c);
    if (!named) setName(uniqueName(nameForHex(c), taken));
  };
  const add = () => {
    const n = name.trim().slice(0, 40);
    if (!hex || !n) return;
    onAdd(uniqueName(n, taken), hex);
    setOpen(false);
  };
  return (
    <span ref={box} className="relative ms-auto flex" onKeyDown={onKeyDown}>
      <button
        type="button"
        onClick={() => {
          setHex("");
          setName("");
          setNamed(false);
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t("catalog.add.colourAdd")}
        className={cx(
          "flex size-6 items-center justify-center rounded-xs hover:bg-hover",
          open ? "text-link" : "text-fg-secondary hover:text-link",
        )}
        data-testid="add-colour"
      >
        <Icon name="palette" />
        <span className="ady-sr-only">{t("catalog.add.colourAdd")}</span>
      </button>
      {open ? (
        <span role="dialog" aria-label={t("catalog.add.colourAdd")} className={cx(panel, "end-0")}>
          <span className="text-xs font-semibold">{t("catalog.add.colourAdd")}</span>
          <ColourFields value={hex} onPick={pick} />
          <label className="flex flex-col gap-0.5">
            <span className="text-2xs font-semibold text-fg-tertiary">
              {t("catalog.add.colourName")}
            </span>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNamed(!!e.target.value.trim());
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder={t("catalog.add.colourNamePh")}
              maxLength={40}
              className="min-h-control rounded-xs border border-control bg-surface px-2 text-xs outline-none focus-visible:outline-2 focus-visible:outline-focus"
            />
          </label>
          <span className="flex justify-end gap-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-xs px-2 py-1 text-xs font-semibold text-fg-secondary hover:bg-hover"
            >
              {t("catalog.rich.cancel")}
            </button>
            <button
              type="button"
              onClick={add}
              disabled={!hex || !name.trim()}
              className="ady-bg-brand rounded-xs px-2.5 py-1 text-xs font-semibold text-on-primary disabled:opacity-50"
            >
              {t("catalog.add.colourAddButton")}
            </button>
          </span>
        </span>
      ) : null}
    </span>
  );
}
